import { useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Printer } from 'lucide-react';
import { useGetMyResultsQuery, getApiError } from '@/store/api';
import { Button } from '@/components/ui/Button';
import { Spinner } from '@/components/ui/Spinner';
import { EmptyState } from '@/components/ui/EmptyState';
import { ReportCardSheet } from '@/pages/exams/ReportCardSheet';

/*
  ═════════════════════════════════════════════════════════════════════════════
  A PARENT'S OWN COPY

  The school could print report cards; a parent could only read one on a
  screen. For a family that wants a copy for a file, or for the next school,
  "look at it on your phone" is not a copy.

  Why it reads the parent's own endpoint and takes no ids on trust
  ───────────────────────────────────────────────────────────────
  The exam and student ids come from the URL, but they are only used to PICK
  from the list the API already decided this parent may see — published exams,
  their own children, resolved from their phone number. An id for another
  family's child simply matches nothing here; it is not a thing this page
  could be talked into fetching.

  Why it is outside the parent tab bar
  ────────────────────────────────────
  It is a document, not a screen. The same reason the school's print page sits
  outside the dashboard shell: there is nothing to hide from the printer
  because there is nothing on the page but the sheet.
  ═════════════════════════════════════════════════════════════════════════════
*/
export function ParentReportCardPage() {
  const { examId = '', studentId = '' } = useParams();
  const navigate = useNavigate();
  const { data: children = [], isLoading, isError, error } = useGetMyResultsQuery();

  const child = children.find((c) => c.studentId === studentId);
  const card = child?.reports.find((r) => r.examId === examId);

  /* The browser keeps its own white page; ours would otherwise be a dark strip
     on it. Restored on the way out. */
  useEffect(() => {
    const previous = document.body.style.background;
    document.body.style.background = '#fff';
    return () => {
      document.body.style.background = previous;
    };
  }, []);

  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-white">
        <Spinner />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#f1f3f6] py-6 text-black">
      <div className="no-print mx-auto mb-5 flex max-w-[820px] items-center gap-2 px-4">
        <Button variant="secondary" size="sm" onClick={() => navigate('/parent/results')}>
          <ArrowLeft size={13} className="mr-1" />
          Back to results
        </Button>
        {card && (
          <Button variant="primary" size="sm" className="ml-auto" onClick={() => window.print()}>
            <Printer size={13} className="mr-1" />
            Print or save as PDF
          </Button>
        )}
      </div>

      {isError ? (
        <p role="alert" className="mx-auto max-w-[820px] px-4 text-[12px] font-medium text-red-700">
          {getApiError(error)}
        </p>
      ) : !card ? (
        <div className="mx-auto max-w-[820px] px-4">
          <EmptyState
            title="That report card is not available"
            description="It may not have been released yet, or it belongs to a different child. Go back and pick one from the list."
          />
        </div>
      ) : (
        <ReportCardSheet card={card} />
      )}
    </div>
  );
}
